import type {
  OrderProductRealizationDto,
  RouteOrderDto,
  TransportProductDto,
  TransportRouteSummaryDto,
  TransportOrderSummaryDto,
} from '../../types/transport';

export interface TransportOrderTruck {
  transport: TransportRouteSummaryDto;
  routeOrder: RouteOrderDto;
  products: TransportProductDto[];
}

export interface TransportOrderGroup {
  orderId: string;
  order: TransportOrderSummaryDto;
  productRealization: OrderProductRealizationDto[];
  trucks: TransportOrderTruck[];
}

export interface TransportOrderGrouping {
  orderGroups: TransportOrderGroup[];
  supplierOnlyTransports: TransportRouteSummaryDto[];
}

function getCanonicalOrderId(routeOrder: RouteOrderDto): string {
  return routeOrder.order.id || routeOrder.orderId;
}

function mergeCargoProduct(
  cargoItem: TransportRouteSummaryDto['cargoItems'][number],
  orderProduct?: TransportProductDto,
): TransportProductDto {
  return {
    ...(orderProduct || {}),
    id: cargoItem.orderProductId || cargoItem.id,
    cargoItemId: cargoItem.id,
    orderId: cargoItem.orderId,
    name: cargoItem.productName || orderProduct?.name || 'Produkt',
    quantity: cargoItem.quantity,
    unit: cargoItem.unit || orderProduct?.unit,
    grossWeight: cargoItem.grossWeight ?? orderProduct?.grossWeight,
    width: cargoItem.width ?? orderProduct?.width,
    height: cargoItem.height ?? orderProduct?.height,
    length: cargoItem.length ?? orderProduct?.length,
    thickness: cargoItem.thickness ?? orderProduct?.thickness,
    piecesPerPackage: cargoItem.piecesPerPackage ?? orderProduct?.piecesPerPackage,
    packageLength: cargoItem.packageLength ?? orderProduct?.packageLength,
    packageWidth: cargoItem.packageWidth ?? orderProduct?.packageWidth,
    packageStackLayers: cargoItem.packageStackLayers ?? orderProduct?.packageStackLayers,
  };
}

export function getTransportProductsForOrder(
  transport: TransportRouteSummaryDto,
  routeOrder: RouteOrderDto,
): TransportProductDto[] {
  const canonicalOrderId = getCanonicalOrderId(routeOrder);
  const cargoItems = transport.cargoItems.filter((item) => item.orderId === canonicalOrderId);
  if (cargoItems.length === 0) {
    // Legacy order link without explicit cargo is interpreted as a full-order
    // reservation by the backend. Show the same product set on the truck row.
    return routeOrder.order.products;
  }

  const orderProductsById = new Map(
    routeOrder.order.products.map((product) => [product.id, product]),
  );
  return cargoItems.map((cargoItem) => mergeCargoProduct(
    cargoItem,
    cargoItem.orderProductId ? orderProductsById.get(cargoItem.orderProductId) : undefined,
  ));
}

export function buildTransportOrderGrouping(
  transports: TransportRouteSummaryDto[],
): TransportOrderGrouping {
  const groupsByOrderId = new Map<string, TransportOrderGroup>();
  const supplierOnlyTransports: TransportRouteSummaryDto[] = [];

  transports.forEach((transport) => {
    if (transport.routeOrders.length === 0) {
      supplierOnlyTransports.push(transport);
      return;
    }

    transport.routeOrders.forEach((routeOrder) => {
      // The nested order is the canonical entity. `routeOrder.orderId` comes
      // from the allocation projection and older/global-create responses may
      // carry a stale alias there. Grouping by the entity ID makes transports
      // created from the global form and from an order-scoped `+` converge on
      // the same accordion.
      const canonicalOrderId = getCanonicalOrderId(routeOrder);
      const existing = groupsByOrderId.get(canonicalOrderId);
      const truck: TransportOrderTruck = {
        transport,
        routeOrder,
        products: getTransportProductsForOrder(transport, routeOrder),
      };

      if (existing) {
        if (!existing.trucks.some(({ transport: groupedTransport }) => groupedTransport.id === transport.id)) {
          existing.trucks.push(truck);
        }
        if (existing.productRealization.length === 0 && routeOrder.productRealization.length > 0) {
          existing.productRealization = routeOrder.productRealization;
        }
        return;
      }

      groupsByOrderId.set(canonicalOrderId, {
        orderId: canonicalOrderId,
        order: routeOrder.order,
        productRealization: routeOrder.productRealization,
        trucks: [truck],
      });
    });
  });

  // Realization is a global server snapshot over every active transport, not
  // a value that can be reconstructed from the currently filtered list. If
  // the API omits it, keep the group empty so a contract break is visible
  // instead of displaying a plausible but filter-dependent balance.
  const orderGroups = Array.from(groupsByOrderId.values());

  return { orderGroups, supplierOnlyTransports };
}
